import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload } from 'lucide-react';
import { applicationApiService } from '../services/applicationApi';
import { AuthLayout, InputField, Checkbox } from '../../components/modern';

interface ApplicationData {
    firstName: string;
    lastName: string;
    email: string;
    resumeFile: File | null;
    linkedinUrl: string;
    referralCode: string;
    agreeToTerms: boolean;
}

interface FieldErrors {
    firstName?: string;
    lastName?: string;
    email?: string;
    resume?: string;
    terms?: string;
}

const ApplicationForm: React.FC = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [applicationData, setApplicationData] = useState<ApplicationData>({
        firstName: '',
        lastName: '',
        email: '',
        resumeFile: null,
        linkedinUrl: '',
        referralCode: '',
        agreeToTerms: false,
    });
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [isLoading, setIsLoading] = useState(false);

    const handleInputChange = (field: keyof ApplicationData, value: string | boolean | File | null) => {
        setApplicationData(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear field error when user starts typing
        if (fieldErrors[field as keyof FieldErrors]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type === 'application/pdf') {
                if (file.size <= 5 * 1024 * 1024) { // 5MB limit
                    handleInputChange('resumeFile', file);
                } else {
                    setFieldErrors(prev => ({
                        ...prev,
                        resume: 'File size must be less than 5MB'
                    }));
                }
            } else {
                setFieldErrors(prev => ({
                    ...prev,
                    resume: 'Only PDF files are allowed'
                }));
            }
        }
    };

    const validateForm = (): boolean => {
        const errors: FieldErrors = {};

        if (!applicationData.firstName.trim()) {
            errors.firstName = 'First name is required';
        }

        if (!applicationData.lastName.trim()) {
            errors.lastName = 'Last name is required';
        }

        if (!applicationData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applicationData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        if (!applicationData.resumeFile && !applicationData.linkedinUrl.trim()) {
            errors.resume = 'Please upload your resume or provide your LinkedIn URL';
        }

        if (!applicationData.agreeToTerms) {
            errors.terms = 'You must agree to the Terms of Use and Privacy Policy';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            // Submit application using API service
            const response = await applicationApiService.submitApplication({
                firstName: applicationData.firstName,
                lastName: applicationData.lastName,
                email: applicationData.email,
                linkedInUrl: applicationData.linkedinUrl || undefined,
                resumeFile: applicationData.resumeFile || undefined,
            });

            // Navigate to OTP verification page with application ID
            navigate('/auth/application-otp', {
                state: {
                    applicationId: response.application_id,
                    email: applicationData.email,
                    firstName: applicationData.firstName
                }
            });
        } catch (error) {
            console.error('Application submission failed:', error);
            setFieldErrors(prev => ({
                ...prev,
                resume: error instanceof Error ? error.message : 'Failed to submit application. Please try again.'
            }));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthLayout
            title="Apply Now"
            subtitle={
                <>
                    Already have an account? <a href="/auth/login" className="text-[#015FA6] font-bold underline decoration-2 underline-offset-4 hover:text-[#043362] transition-colors">Sign in</a>
                </>
            }
            heroVariant="candidate"
        >
            <form onSubmit={handleSubmit} className="flex flex-col gap-5 w-full">
                {/* First Name & Last Name Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <InputField
                        id="firstName"
                        label="First name"
                        type="text"
                        value={applicationData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        placeholder="Ben"
                        error={fieldErrors.firstName}
                        delay={100}
                    />
                    <InputField
                        id="lastName"
                        label="Last name"
                        type="text"
                        value={applicationData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        placeholder="Pucci"
                        error={fieldErrors.lastName}
                        delay={150}
                    />
                </div>

                {/* Email Field */}
                <InputField
                    id="email"
                    label="Email"
                    type="email"
                    value={applicationData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="example@gmail.com"
                    error={fieldErrors.email}
                    delay={200}
                />

                {/* LinkedIn URL */}
                <InputField
                    id="linkedin"
                    label="LinkedIn URL"
                    type="url"
                    value={applicationData.linkedinUrl}
                    onChange={(e) => handleInputChange('linkedinUrl', e.target.value)}
                    placeholder="https://www.linkedin.com/in/yourprofile"
                    delay={250}
                />
                {fieldErrors.resume && (
                    <p className="text-red-500 text-sm -mt-3 font-['Manrope']">
                        {fieldErrors.resume}
                    </p>
                )}

                {/* Upload Resume */}
                <div className="flex flex-col gap-2 animate-fade-in-up opacity-0" style={{ animationDelay: '300ms' }}>
                    <label className="text-base font-semibold text-[#015FA6]/80 font-['Manrope']">
                        Resume (Optional, PDF only)
                    </label>
                    <div className="flex items-center h-[60px] bg-[#F7FAFC] border border-[#CBD5E0] rounded-xl overflow-hidden hover:border-[#015FA6]/50 transition-colors">
                        <input
                            type="text"
                            value={applicationData.resumeFile ? applicationData.resumeFile.name : ''}
                            placeholder="Upload your resume (can be added later)"
                            readOnly
                            className="flex-1 h-full px-5 font-['Manrope'] text-base text-[#414D63] placeholder-[#015FA6]/40 bg-transparent border-none outline-none cursor-default truncate"
                        />
                        <div className="w-px h-6 bg-[#CBD5E0]" />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center justify-center gap-2 px-5 h-full font-['Manrope'] text-base text-[#015FA6] hover:bg-[#015FA6]/5 transition-colors"
                        >
                            <Upload size={18} />
                            Upload
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf"
                            onChange={handleFileChange}
                            className="hidden"
                        />
                    </div>
                </div>

                {/* Referral Code */}
                <InputField
                    id="referralCode"
                    label="Referral Code"
                    type="text"
                    value={applicationData.referralCode}
                    onChange={(e) => handleInputChange('referralCode', e.target.value)}
                    placeholder="Enter referral code"
                    delay={350}
                />

                {/* Terms Checkbox */}
                <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '400ms' }}>
                    <Checkbox
                        checked={applicationData.agreeToTerms}
                        onChange={(checked) => handleInputChange('agreeToTerms', checked)}
                        error={fieldErrors.terms}
                        label={
                            <span className="text-[#015FA6]">
                                I agree to the{' '}
                                <span
                                    onClick={() => navigate('/terms-of-service')}
                                    className="text-[#F59E0B] font-semibold cursor-pointer hover:underline"
                                >
                                    Terms of Use
                                </span> and{' '}
                                <span
                                    onClick={() => navigate('/privacy-policy')}
                                    className="text-[#F59E0B] font-semibold cursor-pointer hover:underline"
                                >
                                    Privacy Policy
                                </span>
                            </span>
                        }
                    />
                </div>

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={isLoading}
                    className="
                        w-full h-[60px] mt-4 rounded-xl font-bold text-lg text-white
                        bg-gradient-to-r from-[#015FA6] to-[#0172C6]
                        hover:shadow-[0_8px_25px_-5px_rgba(1,95,166,0.4)]
                        active:scale-[0.98]
                        transition-all duration-300
                        animate-fade-in-up opacity-0
                        flex items-center justify-center gap-2
                        disabled:opacity-70 disabled:cursor-not-allowed
                    "
                    style={{ animationDelay: '500ms' }}
                >
                    {isLoading ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Submitting...
                        </>
                    ) : (
                        'Sign Up'
                    )}
                </button>
            </form>
        </AuthLayout>
    );
};

export default ApplicationForm;
